import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  Activity, Loader2, RefreshCw, AlertTriangle, CheckCircle2,
  RotateCcw, Zap, Download, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { DateRangePicker } from '../components/ui/date-range-picker';

const STATUS_COLORS = {
  new: '#64748B', downloading: '#38BDF8', transcribing: '#38BDF8',
  translating: '#FBBF24', analyzing: '#22D3C5', voice_analyzing: '#22D3C5',
  completed: '#34D399', failed: '#FB7185',
};
const STATUS_LABELS = {
  new: 'New', downloading: 'Downloading', transcribing: 'Transcribing',
  translating: 'Translating', analyzing: 'AI Analysis', voice_analyzing: 'Voice Analysis',
  completed: 'Completed', failed: 'Failed',
};


export default function ProcessingMonitorPage() {
  const navigate = useNavigate();
  const { isAdmin, isSupervisor } = useAuth();
  const [data, setData] = useState(null);
  const [agentStats, setAgentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [stoppingFetch, setStoppingFetch] = useState(false);

  // Agent-wise processing state
  const [agentSearch, setAgentSearch] = useState('');
  const [availableAgentNames, setAvailableAgentNames] = useState([]);
  const [showAgentSuggestions, setShowAgentSuggestions] = useState(false);
  const [processingAgents, setProcessingAgents] = useState(new Set());
  const [agentBatchSize, setAgentBatchSize] = useState(10);
  const [dateRange, setDateRange] = useState({ from_date: '', to_date: '' });

  const getAgentStageKey = (agentNumber, stage) => `${agentNumber}::${stage}`;

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, agentRes] = await Promise.all([
        api.getProcessingStatus(),
        api.getAgentProcessingStats({ fromDate: dateRange.from_date, toDate: dateRange.to_date }).catch(() => ({ agents: [] }))
      ]);
      setData(statusRes);
      setAgentStats(agentRes.agents || []);
    } catch (err) {
      toast.error('Failed to load processing status');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 12000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (!isAdmin && !isSupervisor) return;
    api.getAgentsList()
      .then((res) => {
        const names = Array.isArray(res?.agents)
          ? res.agents
              .map((a) => (typeof a === 'object' ? (a.agent_name || a.agent_number || '') : String(a)))
              .filter(Boolean)
          : [];
        setAvailableAgentNames(names);
      })
      .catch(() => {});
  }, [isAdmin, isSupervisor]);

  const handleProcessBatch = async (count) => {
    setProcessing(true);
    try {
      const res = await api.processBatch(count);
      toast.success(res.message);
      setTimeout(fetchData, 3000);
    } catch (err) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const handleFetch = async () => {
    setFetching(true);
    try {
      const res = await api.fetchHistorical(30);
      toast.success(res.message);
      setData((prev) => (prev ? { ...prev, polling_active: true, poll_error: null } : prev));
      setTimeout(fetchData, 5000);
    } catch (err) { toast.error('Failed'); }
    finally { setFetching(false); }
  };

    const openCallDetailsInNewTab = (callId, e) => {
    e.stopPropagation();
    const detailPath = `/calls/${encodeURIComponent(callId)}`;

    // Open first to avoid popup blockers, then hydrate auth state in the new tab.
    const newTab = window.open('', '_blank');
    if (!newTab) {
      navigate(detailPath);
      return;
    }

    try {
      const token = sessionStorage.getItem('vcm_token');
      const userData = sessionStorage.getItem('vcm_user');
      if (token) {
        newTab.sessionStorage.setItem('vcm_token', token);
      }
      if (userData) {
        newTab.sessionStorage.setItem('vcm_user', userData);
      }
      newTab.location.href = detailPath;
    } catch (error) {
      newTab.location.href = detailPath;
    }
  };

  const handleRetry = async (callId) => {
    try { await api.retryCall(callId); toast.success('Retry started'); setTimeout(fetchData, 3000); }
    catch (err) { toast.error('Retry failed'); }
  };

  const handleProcessAgent = async (agentName, stage) => {
    const stageKey = getAgentStageKey(agentName, stage);
    setProcessingAgents(prev => new Set(prev).add(stageKey));
    try {
      const res = await api.processAgentCalls(agentName, agentBatchSize, stage, {
        fromDate: dateRange.from_date,
        toDate: dateRange.to_date,
      });
      toast.success(res.message);
      setTimeout(fetchData, 4000);
    } catch (err) {
      toast.error(`Failed to ${stage} ${agentName}`);
    } finally {
      setProcessingAgents(prev => { const n = new Set(prev); n.delete(stageKey); return n; });
    }
  };

  const handleStopFetch = async () => {
    setStoppingFetch(true);
    try {
      const res = await api.stopHistoricalFetch();
      toast.success(res.message || 'Stopping active fetch...');
      setData((prev) => (prev ? { ...prev, polling_active: false } : prev));
      setTimeout(fetchData, 1500);
    } catch (err) {
      toast.error('Failed to stop fetch');
    } finally {
      setStoppingFetch(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const counts = data?.counts || {};
  const pieData = Object.entries(counts).filter(([, v]) => v > 0).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, key: k }));
  const inProgress = data?.in_progress || [];
  const activeProcessing = ['downloading','transcribing','translating','analyzing','voice_analyzing'].reduce((s,k) => s + (counts[k]||0), 0);

  // Filter agents by search
  const filteredAgents = agentStats.filter(a =>
    !agentSearch || (a.agent_name || a.agent_number || '').toLowerCase().includes(agentSearch.toLowerCase())
  );

  const agentQuery = agentSearch.trim().toLowerCase();
  const agentNameSuggestions = availableAgentNames
    .filter((name) => {
      if (!agentQuery) return true;
      return String(name).toLowerCase().includes(agentQuery);
    })
    .slice(0, 8);

  const handleAgentSearchSelect = (name) => {
    setAgentSearch(name);
    setShowAgentSuggestions(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Processing Monitor</h1>
          <p className="text-sm text-muted-foreground mt-1">Pipeline status, auto/manual &amp; agent-wise processing</p>
        </div>
        <Button size="sm" variant="ghost" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Manual actions strip */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold">Manual Processing Controls</p>
            <p className="text-xs text-muted-foreground">Use fetch and batch actions below to control imports and processing.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                {data?.polling_active ? (
                  <Button size="sm" variant="secondary" onClick={handleStopFetch} disabled={stoppingFetch}>
                    {stoppingFetch ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}Stop Fetching
                  </Button>
                ) : (
                  <Button size="sm" variant="secondary" onClick={handleFetch} disabled={fetching}>
                    {fetching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}Fetch Records
                  </Button>
                )}
                {/* <Button size="sm" variant="secondary" onClick={() => handleProcessBatch(5)} disabled={processing}>
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}Transcribe & Analyze 5
                </Button>
                <Button size="sm" variant="secondary" onClick={() => handleProcessBatch(10)} disabled={processing}>
                  <Zap className="w-4 h-4 mr-1" />Transcribe & Analyze 10
                </Button> */}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {Object.entries(counts).map(([status, count]) => (
          <div key={status} className="bg-card border border-border rounded-xl p-3">
            <div className="w-2.5 h-2.5 rounded-full mb-2" style={{ backgroundColor: STATUS_COLORS[status] }} />
            <p className="text-xl font-semibold tabular-nums">{count}</p>
            <p className="text-xs text-muted-foreground">{STATUS_LABELS[status]}</p>
          </div>
        ))}
      </div>

      {/* =========== AGENT-WISE PROCESSING =========== */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Agent-wise Processing</h3>
            <span className="text-xs text-muted-foreground">({filteredAgents.length} agents)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-48">
              <Input
                placeholder="Search agents..."
                value={agentSearch}
                onFocus={() => setShowAgentSuggestions(true)}
                onBlur={() => setTimeout(() => setShowAgentSuggestions(false), 120)}
                onChange={(e) => {
                  setAgentSearch(e.target.value);
                  setShowAgentSuggestions(true);
                }}
                className="bg-background h-8 text-xs"
                data-testid="agent-process-search"
              />
              {showAgentSuggestions && agentNameSuggestions.length > 0 && (
                <div className="absolute left-0 top-full z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-border bg-background">
                  {agentNameSuggestions.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="w-full px-3 py-2 text-left text-xs text-foreground hover:bg-accent/60"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAgentSearchSelect(name);
                      }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <DateRangePicker
              value={dateRange}
              showValue={!!(dateRange.from_date || dateRange.to_date)}
              placeholder="Date Range"
              onChange={(range) => setDateRange({ from_date: range.from_date, to_date: range.to_date })}
              onClear={() => setDateRange({ from_date: '', to_date: '' })}
              buttonClassName="h-8 text-xs min-w-0"
            />
            <select
              value={agentBatchSize}
              onChange={(e) => setAgentBatchSize(Number(e.target.value))}
              className="bg-background border border-border rounded-lg px-2 py-1 text-xs h-8"
              data-testid="agent-batch-size-select"
            >
              <option value={5}>5 per click</option>
              <option value={10}>10 per click</option>
              <option value={15}>15 per click</option>
              <option value={20}>20 per click</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto max-h-[420px]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-3 py-2">Agent</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-3 py-2">Total</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-3 py-2">New</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-3 py-2">Processing</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-3 py-2">Completed</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-3 py-2">Failed</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-3 py-2">Processable</th>
                {(isAdmin || isSupervisor) && (
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase px-3 py-2">Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map(agent => {
                const isTranscribingNow = processingAgents.has(getAgentStageKey(agent.agent_number, 'transcribe'));
                return (
                  <tr key={agent.agent_number} className="border-b border-border/50 hover:bg-white/[0.02]">
                    <td className="px-3 py-2.5 text-sm font-medium">{agent.agent_name || agent.agent_number}</td>
                    <td className="px-3 py-2.5 text-sm text-right tabular-nums text-muted-foreground">{agent.total}</td>
                    <td className="px-3 py-2.5 text-sm text-right tabular-nums">
                      <span className={agent.new > 0 ? 'text-slate-300' : 'text-muted-foreground'}>{agent.new}</span>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right tabular-nums">
                      {agent.processing > 0 ? <span className="text-sky-300 status-pulse">{agent.processing}</span> : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right tabular-nums">
                      <span className={agent.completed > 0 ? 'text-emerald-300' : 'text-muted-foreground'}>{agent.completed}</span>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right tabular-nums">
                      {agent.failed > 0 ? <span className="text-rose-300">{agent.failed}</span> : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right tabular-nums">
                      <span className={agent.processable > 0 ? 'text-teal-300 font-medium' : 'text-muted-foreground'}>{agent.processable}</span>
                    </td>
                    {(isAdmin || isSupervisor) && (
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant={agent.processable > 0 ? "default" : "ghost"}
                            disabled={agent.processable === 0 || isTranscribingNow}
                            onClick={() => handleProcessAgent(agent.agent_number, 'transcribe')}
                            data-testid={`process-agent-transcribe-${agent.agent_number}`}
                            className="h-7 text-xs"
                          >
                            {isTranscribingNow
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <><Zap className="w-3.5 h-3.5 mr-1" />Transcribe</>}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filteredAgents.length === 0 && (
                <tr><td colSpan={(isAdmin || isSupervisor) ? 8 : 7} className="text-center py-6 text-sm text-muted-foreground">No agents found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pipeline chart + In Progress + Failures */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pipeline Chart */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Pipeline Distribution</h3>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={75} innerRadius={35} label={false}>
                    {pieData.map(entry => <Cell key={entry.key} fill={STATUS_COLORS[entry.key]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-center">
                {pieData.map(({ name, key, value }) => (
                  <div key={key} className="flex flex-col items-center gap-1 p-1.5 bg-card/50 rounded-lg border">
                    <div 
                      className="w-3 h-3 rounded-full mx-auto" 
                      style={{ backgroundColor: STATUS_COLORS[key] }}
                    />
                    <span className="font-mono font-semibold">{value}</span>
                    <span className="text-muted-foreground leading-tight">{name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-sm text-muted-foreground text-center py-8">No data</p>}
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${data?.polling_active ? 'bg-amber-400 status-pulse' : 'bg-emerald-400'}`} />
              <span className="text-xs text-muted-foreground">{data?.polling_active ? 'Polling...' : `Last poll: ${data?.last_poll ? new Date(data.last_poll).toLocaleString() : 'Never'}`}</span>
            </div>
          </div>
        </div>

        {/* In Progress */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-sky-400" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">In Progress ({activeProcessing})</h3>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {inProgress.length === 0
              ? <p className="text-xs text-muted-foreground text-center py-4">None</p>
              : inProgress.map(c => (
                <div key={c.call_id||c.id} className="flex items-center gap-2 px-3 py-2 rounded bg-sky-500/5 border border-sky-500/20 cursor-pointer hover:bg-sky-500/10" 
                 onClick={(e) => openCallDetailsInNewTab(c.call_id, e)}>
                  <div className="w-2 h-2 rounded-full bg-sky-400 status-pulse" />
                  <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{c.agent_name}</p><p className="text-[10px] text-muted-foreground font-mono">{c.call_id}</p></div>
                  <span className="text-[10px] text-sky-300">{c.processing_status}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Failures */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Failures ({counts.failed||0})</h3>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {(data?.recent_failures||[]).length === 0
              ? <p className="text-xs text-muted-foreground text-center py-4">None</p>
              : (data?.recent_failures||[]).map(c => (
                <div key={c.call_id||c.id} title={c.error} className="flex items-center gap-2 px-3 py-2 rounded bg-rose-500/5 border border-rose-500/20">
                  <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{c.agent_name}</p><p className="text-[10px] text-rose-300 truncate">{c.error}</p></div>
                  <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => handleRetry(c.call_id)}><RotateCcw className="w-3 h-3" /></Button>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Recent Completions */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Recent Completions</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {(data?.recent_completions||[]).map(c => {
            const score = c.overall_score != null ? Number(c.overall_score) : null;
            return (
              <div key={c.call_id||c.id} className="flex items-center gap-3 px-3 py-2 rounded bg-emerald-500/5 border border-emerald-500/20 cursor-pointer hover:bg-emerald-500/10" 
                onClick={(e) => openCallDetailsInNewTab(c.call_id, e)}>
                <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{c.agent_name}</p><p className="text-[10px] text-muted-foreground">{c.date}</p></div>
                {score != null && <span className={`text-xs font-medium px-2 py-0.5 rounded ${score>=75?'score-good':score>=60?'score-review':'score-critical'}`}>{score}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
